import { parsePosix, posixTransitionsForYear, posixTypeAt, type PosixType } from "./posix";

export interface ZoneType {
  utoff: number;
  isdst: 0 | 1;
  abbr: string;
}

export interface RawZone {
  t: number[]; // transition times (UTC seconds)
  i: number[]; // type index per transition
  y: [number, number, string][]; // [utoff, isdst, abbr]
  f: string; // POSIX TZ footer
}

export interface TzData {
  version: string;
  zones: Record<string, RawZone>;
  links: Record<string, string>;
}

export interface Transition {
  at: number; // UTC seconds
  before: ZoneType;
  after: ZoneType;
}

export class Zone {
  readonly id: string;
  readonly times: number[];
  readonly types: ZoneType[];
  private readonly idx: number[];
  private readonly posix;
  private readonly firstType: ZoneType;

  constructor(id: string, raw: RawZone) {
    this.id = id;
    this.times = raw.t;
    this.idx = raw.i;
    this.types = raw.y.map(([utoff, isdst, abbr]) => ({ utoff, isdst: isdst ? 1 : 0, abbr }));
    this.posix = raw.f ? parsePosix(raw.f) : null;
    // RFC 8536 §3.2: times before the first transition use type 0 (zic
    // guarantees type 0 is the pre-transition type).
    this.firstType = this.types[0];
  }

  /** Type (offset/dst/abbr) in effect at a UTC instant. */
  typeAt(utc: number): ZoneType {
    const t = this.times;
    if (t.length === 0 || utc < t[0]) {
      return t.length === 0 && this.posix ? this.posix.std : this.firstType;
    }
    if (utc >= t[t.length - 1] && this.posix) {
      return posixTypeAt(this.posix, utc);
    }
    // binary search: last transition <= utc
    let lo = 0;
    let hi = t.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (t[mid] <= utc) lo = mid;
      else hi = mid - 1;
    }
    return this.types[this.idx[lo]];
  }

  offsetAt(utc: number): number {
    return this.typeAt(utc).utoff;
  }

  /** Transitions with `from <= at < to`, in order. */
  transitionsBetween(from: number, to: number): Transition[] {
    const out: Transition[] = [];
    const t = this.times;
    let prev: ZoneType = this.firstType;
    for (let k = 0; k < t.length; k++) {
      const cur = this.types[this.idx[k]];
      if (t[k] >= from && t[k] < to && differs(prev, cur)) out.push({ at: t[k], before: prev, after: cur });
      prev = cur;
    }
    if (this.posix && this.posix.dst) {
      const lastExplicit = t.length ? t[t.length - 1] : -Infinity;
      const y0 = new Date(Math.max(from, lastExplicit) * 1000).getUTCFullYear() - 1;
      const y1 = new Date(to * 1000).getUTCFullYear() + 1;
      // type in effect right after the last explicit transition
      let before: ZoneType = t.length ? this.types[this.idx[t.length - 1]] : this.posix.std;
      for (let y = y0; y <= y1; y++) {
        for (const tr of posixTransitionsForYear(this.posix, y)) {
          if (tr.at <= lastExplicit) continue;
          const after = toType(tr.type);
          if (tr.at >= from && tr.at < to && differs(before, after)) out.push({ at: tr.at, before, after });
          if (tr.at > lastExplicit) before = after;
        }
      }
    }
    out.sort((a, b) => a.at - b.at);
    return out;
  }

  /** First transition strictly after `utc`, looking ahead at most `horizon` seconds. */
  nextTransition(utc: number, horizon = 20 * 366 * 86400): Transition | null {
    const list = this.transitionsBetween(utc + 1, utc + horizon);
    return list[0] ?? null;
  }

  /** Last transition at or before `utc`, looking back at most `horizon` seconds. */
  prevTransition(utc: number, horizon = 20 * 366 * 86400): Transition | null {
    const list = this.transitionsBetween(utc - horizon, utc + 1);
    return list[list.length - 1] ?? null;
  }

  /**
   * Resolve a local wall-clock time (seconds since 1970-01-01T00:00 *local*,
   * i.e. computed with Date.UTC on the wall-clock fields) to UTC instants.
   */
  resolveLocal(local: number): LocalResolution {
    const candidates = new Set<number>();
    for (const probe of [-2 * 86400, -86400, 0, 86400, 2 * 86400]) {
      const off = this.offsetAt(local + probe);
      const utc = local - off;
      if (this.offsetAt(utc) === off) candidates.add(utc);
    }
    const list = [...candidates].sort((a, b) => a - b);
    if (list.length === 1) return { kind: "unique", utc: list[0], candidates: list };
    if (list.length > 1) return { kind: "ambiguous", utc: list[0], candidates: list };
    // Gap: the wall clock time was skipped. Use the offset in effect just
    // before the gap so the result lands after the transition (e.g. 02:30
    // during spring-forward becomes 03:30).
    const tr = this.nextTransition(local - 86400 * 2, 86400 * 4);
    const offBefore = tr ? tr.before.utoff : this.offsetAt(local);
    return { kind: "gap", utc: local - offBefore, candidates: [] };
  }
}

export interface LocalResolution {
  kind: "unique" | "ambiguous" | "gap";
  utc: number;
  candidates: number[];
}

function toType(p: PosixType): ZoneType {
  return { utoff: p.utoff, isdst: p.isdst, abbr: p.abbr };
}

function differs(a: ZoneType, b: ZoneType): boolean {
  return a.utoff !== b.utoff || a.isdst !== b.isdst || a.abbr !== b.abbr;
}

export class TzDatabase {
  readonly version: string;
  private readonly data: TzData;
  private readonly cache = new Map<string, Zone>();

  constructor(data: TzData) {
    this.data = data;
    this.version = data.version;
  }

  /** Canonical id for `id` (follows links), or null if unknown. */
  canonical(id: string): string | null {
    if (id in this.data.zones) return id;
    const target = this.data.links[id];
    return target && target in this.data.zones ? target : null;
  }

  has(id: string): boolean {
    return this.canonical(id) !== null;
  }

  zone(id: string): Zone {
    const c = this.canonical(id);
    if (!c) throw new Error(`unknown zone ${id}`);
    let z = this.cache.get(c);
    if (!z) {
      z = new Zone(c, this.data.zones[c]);
      this.cache.set(c, z);
    }
    return z;
  }

  ids(): string[] {
    return Object.keys(this.data.zones);
  }

  linkIds(): string[] {
    return Object.keys(this.data.links).filter((l) => this.canonical(l) !== null);
  }

  /** The raw compiled data for a zone (follows links). */
  raw(id: string): RawZone {
    const c = this.canonical(id);
    if (!c) throw new Error(`unknown zone ${id}`);
    return this.data.zones[c];
  }
}
